// ---------------------------------------------------------------------------
// The page behind the unsubscribe link in every email (OPT1, R13)
//
// OPT1 (Ferenc 29.10.: "Signierter Link, kein Formular"): the link carries the
// recipient's address sealed by the server (?t=); the page sends that on load
// and never sees the address. There is no field to type an address into: with
// one, anybody could stop anybody's emails. A visit without a link names the
// ways out instead.
//
// A reply is named as a way out, worded as a request to the sender and not as
// an unsubscribe: a reply in our thread stops that sequence, but it reaches
// the Do Not Contact list only where the campaign drafts replies with the
// agent (reply-agent.ts, opt_out); otherwise a person reads it.
//
// R13 rebuilds it on the design system's AuthLayout (standalone-page.ts); the
// states and their words stay as OPT1 made them (tests/dom/opt-out-page.dom.ts):
//   - while the link is sent: "Processing your request…", announced as a
//     status (a loading Callout);
//   - "You're unsubscribed", and the toast;
//   - refused for a reason that can pass (a limit, a failed write, no
//     connection): "Something went wrong", the reason in a critical Callout
//     (announced) with "Try again", which sends the same link again. Its
//     button goes while that runs, so the page's title takes the focus: it
//     stays through every state (found in Chrome: the focus fell to <body>);
//   - a link the server will not take, an old one after its window, or none:
//     what happened and the ways out, with no retry that could not work.
// Every state has the page's title as its h1, which the router can focus.
//
// A LitElement with light DOM, so the page holds its own state without
// functional-lit; OptOutPage() is the template the router's map renders.
// ---------------------------------------------------------------------------

import { html, LitElement } from 'lit';

import { button } from '../design/button.js';
import { callout } from '../design/callout.js';
import { OPT_OUT_FAILED, type OptOutLink, type OptOutLinkProblem, optOut, readOptOutLink } from '../suppression-api.js';
import { toast } from '../toast.js';
import { standalonePage } from './standalone-page.js';

type Phase = 'working' | 'done' | 'failed' | OptOutLinkProblem | 'no_link';

const REPLY = 'You can also reply to one of our emails and ask us to stop.';

/** What a visit that cannot unsubscribe anybody says, with the way out. */
const NOTICES: Record<OptOutLinkProblem | 'no_link', { title: string; lines: string[] }> = {
  no_link: {
    title: 'Unsubscribe',
    lines: ['To stop our emails, open the unsubscribe link at the bottom of any email we sent you.', REPLY],
  },
  link_invalid: {
    title: 'This link does not work',
    lines: [
      'It may have been cut short or changed on its way here. Open the unsubscribe link at the bottom of one of our emails again.',
      REPLY,
    ],
  },
  link_expired: {
    title: 'This link has expired',
    lines: [
      'Links in our older emails no longer unsubscribe. Open the unsubscribe link in a more recent email from us.',
      REPLY,
    ],
  },
};

export class SdrOptOutPage extends LitElement {
  static override properties = { phase: { state: true }, reason: { state: true } };

  declare phase: Phase;
  declare reason: string;

  /** The link in the address, read once when the page opens. */
  private readonly link: OptOutLink | null;
  private started = false;

  constructor() {
    super();
    this.link = readOptOutLink(window.location.search);
    this.phase = this.link ? 'working' : 'no_link';
    this.reason = '';
  }

  override createRenderRoot() {
    return this;
  }

  // ── One click: the link's token is sent when the page opens, once ──
  override connectedCallback() {
    super.connectedCallback();
    if (this.started || !this.link) return;
    this.started = true;
    void this.submit(this.link);
  }

  /** Try again: the same link once more; the title keeps the keyboard's place, since the button goes. */
  private retry(from: OptOutLink) {
    void this.submit(from);
    void this.updateComplete.then(() => this.querySelector<HTMLElement>('h1')?.focus({ preventScroll: true }));
  }

  private async submit(from: OptOutLink) {
    this.phase = 'working';
    try {
      // POST /api/opt-out (R11c): no account needed, and it works during a migration hold.
      await optOut(from);
      this.phase = 'done';
      toast.success('You will no longer receive emails from us.');
    } catch (e) {
      const failure = e as { linkProblem?: unknown; message?: unknown } | null;
      if (failure?.linkProblem === 'link_invalid' || failure?.linkProblem === 'link_expired') {
        this.phase = failure.linkProblem;
        return;
      }
      this.reason = typeof failure?.message === 'string' && failure.message ? failure.message : OPT_OUT_FAILED;
      this.phase = 'failed';
    }
  }

  override render() {
    const phase = this.phase;
    if (phase === 'done') {
      return standalonePage({
        title: "You're unsubscribed",
        description: 'You will no longer receive emails from us. If you change your mind, you can contact us anytime.',
      });
    }
    if (phase === 'working') {
      return standalonePage(
        { title: 'Unsubscribe' },
        callout({ tone: 'info', loading: true }, 'Processing your request…'),
      );
    }
    if (phase === 'failed') {
      const link = this.link;
      return standalonePage(
        { title: 'Something went wrong' },
        callout(
          { tone: 'critical', actions: button({ size: 'sm', onClick: () => link && this.retry(link) }, 'Try again') },
          this.reason,
        ),
      );
    }
    const notice = NOTICES[phase];
    return standalonePage({ title: notice.title, description: notice.lines.join(' ') });
  }
}

if (!customElements.get('sdr-opt-out-page')) customElements.define('sdr-opt-out-page', SdrOptOutPage);

/** The opt-out page, as the router's map renders it. */
export const OptOutPage = () => html`<sdr-opt-out-page></sdr-opt-out-page>`;
